import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators, FormGroup } from '@angular/forms';
import { FieldDef, FieldDefType } from '@core/models';
import { TsButtonModule } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { NgxMaskDirective } from 'ngx-mask';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BehaviorSubject, debounceTime, noop, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LookupService } from '@services/lookup.service';
import { Store } from '@ngxs/store';
import { DropdownOptionNames, FieldDefsState } from '@state/fields/state';
import { BulkEditService, BulkProjectUpdateItem } from '@shared/services/bulk-edit.service';
import { PusherService } from '@core/services';
import { TsSnackbarService } from 'tsui';
import { TasksService } from '@shared/modules/tasks/services/tasks.service';
import { TaskState } from '@tasks/state/state';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'bulk-edit-modal',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    FormsModule,
    ReactiveFormsModule,
    TsSelectModule,
    AsyncPipe,
    NgSwitch,
    NgSwitchCase,
    MatFormFieldModule,
    MatInputModule,
    NgxMaskDirective,
    CurrencyMaskModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
  ],
  templateUrl: './bulk-edit-modal.component.html',
  styleUrls: ['./bulk-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkEditModalComponent implements OnInit {
  protected dialogRef = inject(DialogRef);
  public dialogData = inject<{
    fields: FieldDef[];
    values?: any[];
    items: Array<{ id: number | string; title?: string }>;
    related_to?: RelatedToType.Project | RelatedToType.Task;
    hasNestedFiltersOn?: boolean;
  }>(DIALOG_DATA);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private store = inject(Store);
  private bulkEditService = inject(BulkEditService);
  private tasksService = inject(TasksService);
  private pusherService = inject(PusherService);
  private snackBar = inject(TsSnackbarService);

  protected bulkEditForm: FormGroup<{ field: FormControl<string | null>; value: FormControl<any> }> =
    this.formBuilder.group({
      field: this.formBuilder.control<string | null>(null, { validators: [Validators.required] }),
      value: this.formBuilder.control<any>(null, { validators: [Validators.required] }),
    });

  protected recordSearch = new FormControl<string>('');
  protected lookupLoading = new BehaviorSubject<boolean>(false);
  protected lookupResults = new BehaviorSubject<{ id: any; label: string }[]>([]);
  protected ui = new BehaviorSubject<{ loading: boolean; processing: boolean }>({ loading: false, processing: false });
  protected progressMessages = new BehaviorSubject<string[]>([]);
  private activeChannelName: string | null = null;
  public static readonly BULK_EDIT_CHANNEL_KEY = 'bulk_edit_channel';

  ngOnInit(): void {
    this.bulkEditForm
      .get('field')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.bulkEditForm.get('value').reset();
        this.recordSearch.setValue('');
        this.lookupResults.next([]);
        this.cdr.detectChanges();
      });

    this.recordSearch.valueChanges
      .pipe(
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.lookupLoading.next(true)),
        switchMap((query: string) => {
          const field = this.selectedField;
          if (!field || field.type !== 'record') {
            return of([]);
          }
          return this.lookupService.lookFor({ query: query ?? '', relatedTo: field.meta?.record_type as any });
        }),
        tap(() => this.lookupLoading.next(false)),
      )
      .subscribe((results: any) => {
        const normalized = Array.isArray(results)
          ? results.map((r: any) => ({ id: r && 'id' in r ? r.id : r, label: r && 'label' in r ? r.label : String(r) }))
          : [];
        this.lookupResults.next(normalized);
        this.cdr.detectChanges();
      }, noop);
  }

  get selectedField(): FieldDef | null {
    const fieldName = this.bulkEditForm.get('field')?.value;
    if (!fieldName) return null;
    return this.dialogData.fields?.find((f) => f.name === fieldName) ?? null;
  }

  get fieldsToDisplay(): FieldDef[] {
    return (this.dialogData.fields || []).filter((f) => f.type !== 'tags');
  }

  get inputType(): string {
    const type = this.selectedField?.type as FieldDefType;
    switch (type) {
      case 'url':
        return 'url';
      case 'money':
        return 'money';
      case 'number':
      case 'decimal':
      case 'percent':
      case 'year':
      case 'id':
        return 'number';
      default:
        return 'text';
    }
  }

  getSelectItems(): { key: any; label: string }[] {
    if (!this.selectedField) return [];

    if (this.selectedField.type === 'checkbox') {
      return [
        { key: true, label: 'Yes' },
        { key: false, label: 'No' },
      ];
    }

    const optionName = this.selectedField.meta?.option_name as DropdownOptionNames;

    if (optionName) {
      const dropdownOptions = (
        this.store.selectSnapshot(FieldDefsState.selectDropdownOptionsBy(optionName)) as any[] | undefined
      ).filter((option) => option?.related_to_type === this.selectedField.related_to_type && !option?.hidden);

      if (dropdownOptions && Array.isArray(dropdownOptions)) {
        return dropdownOptions.map((o: any) => ({ key: o.id, label: o.name }));
      }
    }

    let options = this.selectedField.meta?.options ?? [];

    if (this.selectedField.name === 'task_section_titles') {
      options = this.store
        .selectSnapshot(TaskState.getTaskTags)
        .map((x) => ({
          label: x.title,
          value: x.title.toString(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (this.selectedField.name === 'contact_ids') {
      options = this.store
        .selectSnapshot(TaskState.getTaskContacts)
        .map((x) => ({
          label: x.name,
          value: x.id.toString(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return (options as any[]).map((opt: any) => {
      if (opt && typeof opt === 'object') {
        const value = 'value' in opt ? opt.value : 'id' in opt ? opt.id : opt;
        const label = 'label' in opt ? opt.label : 'name' in opt ? opt.name : String(value);
        return { key: value, label };
      }

      return { key: opt, label: String(opt) };
    });
  }

  private getRequestMap() {
    return {
      [RelatedToType.Project]: (items: BulkProjectUpdateItem[]) => this.bulkEditService.createBulkUpdateAction(items),
      [RelatedToType.Task]: (items: BulkProjectUpdateItem[]) => this.tasksService.asyncBulkUpdateTask(items),
    };
  }

  update(): void {
    if (this.bulkEditForm.invalid) return;

    const fieldName = this.bulkEditForm.value.field as string;
    let fieldValue = this.bulkEditForm.value.value as any;
    const field = this.selectedField;

    if (this.dialogData.related_to === RelatedToType.Task) {
      fieldValue = this.transformTaskFieldValue(fieldName as string, fieldValue as string);
    } else if (field?.type === 'select' && field?.meta?.multi_select) {
      // Multi-select fields expect an array payload
      fieldValue = Array.isArray(fieldValue) ? fieldValue : fieldValue != null ? [fieldValue] : [];
    }

    const items: BulkProjectUpdateItem[] = (this.dialogData.items || []).map((p) => ({
      id: String(p.id),
      [fieldName]: fieldValue,
    }));

    this.bulkEditForm.disable({ emitEvent: false });
    this.recordSearch.disable({ emitEvent: false });
    this.ui.next({ loading: false, processing: true });
    this.progressMessages.next(['Starting bulk update...']);
    this.cdr.detectChanges();

    const relatedTo = this.dialogData.related_to ?? RelatedToType.Project;
    const requestMap = this.getRequestMap();
    const requestFn = requestMap[relatedTo];
    const apiResponseKey = `${relatedTo?.toLowerCase()}_bulk_action`;

    requestFn(items)
      .pipe(
        tap((res) => {
          const channel = (res as any)?.[apiResponseKey]?.channel?.trim();
          this.activeChannelName = channel;
          this.progressMessages.next(['Bulk action initiated. Waiting for updates...']);

          if (channel) {
            const channelData = {
              channelName: channel,
              relatedTo: relatedTo,
              timestamp: Date.now(),
            };
            localStorage.setItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY, JSON.stringify(channelData));
          }
          this.listenToBulkChannel(channel);
        }),
      )
      .subscribe({
        error: (err) => {
          this.ui.next({ loading: false, processing: false });
          this.snackBar.open('Failed to start bulk update', 'error', null, { duration: 3000 });
          this.bulkEditForm.enable({ emitEvent: false });
          this.recordSearch.enable({ emitEvent: false });
          this.cdr.detectChanges();
        },
      });
  }

  private listenToBulkChannel(channelName: string): void {
    this.pusherService
      .listenToChannel({ channelName, eventName: 'progress' })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(
          (data: {
            status: string;
            message?: string;
            results?: Array<{ success: boolean; project_id: number; message: string }>;
          }) => {
            this.progressMessages.next([data.status]);

            if (data.status === 'Complete') {
              const successful = data.results.filter((result) => result.success);
              const failed = data.results.filter((result) => !result.success);

              localStorage.removeItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);

              if (failed.length === 0) {
                this.snackBar.open('Bulk update finished successfully', 'info', null, {
                  duration: 3000,
                });
                setTimeout(() => {
                  this.discard(true);
                }, 1000);
              } else if (successful.length === 0) {
                this.snackBar.open('Bulk update failed for all items', 'error', null, {
                  duration: 5000,
                });
                setTimeout(() => {
                  this.discard(true);
                }, 1000);
              } else {
                this.snackBar.open(
                  `Bulk update finished: ${successful.length} succeeded, ${failed.length} failed`,
                  'warning',
                  null,
                  {
                    duration: 5000,
                  },
                );
                setTimeout(() => {
                  this.discard(true);
                }, 1000);
              }
            } else if (data.status === 'An error occurred') {
              localStorage.removeItem(BulkEditModalComponent.BULK_EDIT_CHANNEL_KEY);
              this.snackBar.open('Bulk update failed', 'error', null, {
                duration: 3000,
              });
              this.progressMessages.next([]);
            }
          },
        ),
      )
      .subscribe(noop);
  }

  getMaskForNumberDecimal(field: FieldDef): string {
    if (!field.meta.scale) {
      return '0*';
    }

    return `0*.${Array(field.meta.scale).fill(0).join('')}`;
  }

  getCurrencyMaskOptions(field: FieldDef): any {
    return { align: 'left', precision: field?.meta?.scale || 0, prefix: '$' };
  }

  onDateChange(event: any): void {
    const value = event?.value;
    try {
      const formatted = value?.format ? value.format('YYYY-MM-DD') : value;
      this.bulkEditForm.get('value')?.setValue(formatted);
    } catch {
      this.bulkEditForm.get('value')?.setValue(value);
    }
  }

  discard(result: boolean = false): void {
    this.dialogRef.close(result);
  }

  private transformTaskFieldValue(fieldName: string, value: string): any {
    if (['task_section_titles'].includes(fieldName)) {
      return [value];
    }

    return value;
  }
}
